import { LinkRounded } from '@mui/icons-material';
import {
    Alert,
    Button,
    DialogActions,
    DialogContent,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import { useState } from 'react';
import EmployeePortalClient, { EmployeeInvitationAdminStatus } from '../apiClients/EmployeePortalClient';
import BusySpinner from './BusySpinner';
import IconButtonEx from './IconButtonEx';
import { ResponsiveDialog, ResponsiveDialogHeader } from './responsive';

export interface EmployeePortalAccessActionProps {
    talentId: string;
    talentName: string;
    deliveryEmail?: string;
}

const EmployeePortalAccessAction = ({ talentId, talentName, deliveryEmail = '' }: EmployeePortalAccessActionProps) => {
    const [open, setOpen] = useState(false);
    const [busy, setBusy] = useState(false);
    const [status, setStatus] = useState<EmployeeInvitationAdminStatus | null>(null);
    const [email, setEmail] = useState(deliveryEmail);
    const [revokeReason, setRevokeReason] = useState('Administrator requested employee portal access recovery.');
    const [message, setMessage] = useState('');
    const [error, setError] = useState('');

    const load = async () => {
        setBusy(true);
        setError('');
        try {
            const history = await new EmployeePortalClient().GetInvitationStatusForTalent(talentId);
            const latest = history[0] ?? null;
            setStatus(latest);
            if (!email && latest?.DeliveryEmail)
                setEmail(latest.DeliveryEmail);
        } catch {
            setError('Employee portal access status could not be loaded.');
        } finally {
            setBusy(false);
        }
    };

    const show = () => {
        setOpen(true);
        setMessage('');
        void load();
    };

    const run = async (operation: () => Promise<void>, success: string) => {
        setBusy(true);
        setMessage('');
        setError('');
        try {
            await operation();
            setMessage(success);
        } catch {
            setError('That employee portal access action could not be completed. No access was changed.');
            setBusy(false);
            return;
        }
        try {
            const history = await new EmployeePortalClient().GetInvitationStatusForTalent(talentId);
            setStatus(history[0] ?? null);
        } catch {
            setError('The access action completed, but the latest status could not be refreshed. Close and reopen this dialog to reload it.');
        } finally {
            setBusy(false);
        }
    };

    const accessState = status?.AccessStatus ?? 'NotConnected';
    const invitationState = status?.Status ?? 'NotInvited';
    const displayState = accessState === 'Active'
        ? 'Connected'
        : accessState === 'Revoked'
            ? 'Access revoked'
            : invitationState;
    const canInvite = accessState === 'Revoked'
        || (accessState === 'NotConnected'
            && ['NotInvited', 'Expired', 'Revoked', 'Rejected'].includes(invitationState));
    const canManagePending = accessState === 'NotConnected' && invitationState === 'Pending';
    const canRecover = accessState === 'Active';

    return <>
        <IconButtonEx
            size="small"
            hoverContent={`Manage personal development portal access for ${talentName}`}
            aria-label={`Manage personal development portal access for ${talentName}`}
            onClick={show}
        >
            <LinkRounded fontSize="small" />
        </IconButtonEx>
        <ResponsiveDialog
            open={open}
            onClose={() => !busy && setOpen(false)}
            fullWidth
            maxWidth="sm"
            tabletFullScreen="never"
            aria-labelledby="employee-portal-access-title"
        >
            <ResponsiveDialogHeader
                id="employee-portal-access-title"
                title={`Personal development access · ${talentName}`}
                onClose={() => !busy && setOpen(false)}
            />
            <DialogContent dividers>
                <BusySpinner busy={busy}>
                    <Stack spacing={2}>
                        <Alert severity="info">
                            This invitation connects the person to their own published development history. It does not grant company workspace access. The delivery email is not forced as their account email.
                        </Alert>
                        {error && <Alert severity="error">{error}</Alert>}
                        {message && <Alert severity="success">{message}</Alert>}
                        {status && <>
                            <Stack spacing={0.5}>
                                <Typography variant="caption" color="text.secondary">Access state</Typography>
                                <Typography fontWeight={800}>{displayState}</Typography>
                            </Stack>
                            {status.DeliveryEmail && <Stack spacing={0.5}>
                                <Typography variant="caption" color="text.secondary">Invitation delivery address</Typography>
                                <Typography sx={{ overflowWrap: 'anywhere' }}>{status.DeliveryEmail}</Typography>
                            </Stack>}
                            {canRecover && <Alert severity="success">
                                Portal access is connected. Use recovery only for a mistaken claim; the server records the reason in its access audit.
                            </Alert>}
                        </>}
                        {canInvite && <TextField
                            label="Invitation delivery email"
                            type="email"
                            value={email}
                            onChange={event => setEmail(event.target.value)}
                            autoComplete="email"
                            required
                            fullWidth
                            helperText="The recipient may sign in with a different verified Synergist Pro account."
                        />}
                        {(canManagePending || canRecover) && <TextField
                            label="Reason for revoking access"
                            value={revokeReason}
                            onChange={event => setRevokeReason(event.target.value)}
                            required
                            fullWidth
                            multiline
                            minRows={2}
                            inputProps={{ maxLength: 500 }}
                        />}
                    </Stack>
                </BusySpinner>
            </DialogContent>
                <DialogActions sx={{ flexWrap: 'wrap', gap: 1, '& > :not(:first-of-type)': { ml: 0 }, '& .MuiButton-root': { minHeight: 44 } }}>
                {canInvite && <Button variant="contained" onClick={() => run(
                    () => new EmployeePortalClient().InviteReviewSubject(talentId, email.trim()).then(() => undefined),
                    'A secure personal-development invitation was sent.',
                )} disabled={busy || !/^\S+@\S+\.\S+$/.test(email.trim())}>Send invitation</Button>}
                {canManagePending && <>
                    <Button variant="contained" disabled={busy} onClick={() => run(
                        () => new EmployeePortalClient().ResendInvitation(talentId).then(() => undefined),
                        'The invitation was sent again.',
                    )}>Resend</Button>
                    <Button color="error" disabled={busy || !revokeReason.trim()} onClick={() => run(
                        () => new EmployeePortalClient().RevokeAccess(talentId, revokeReason.trim()),
                        'The invitation was revoked.',
                    )}>Revoke</Button>
                </>}
                {canRecover && <Button color="warning" disabled={busy || !revokeReason.trim()} onClick={() => run(
                    () => new EmployeePortalClient().RevokeAccess(talentId, revokeReason.trim()),
                    'Access was revoked with an audit reason. You can now send a new invitation to reconnect the correct identity.',
                )}>Revoke for recovery / relink</Button>}
                <Button disabled={busy} onClick={() => setOpen(false)}>Close</Button>
            </DialogActions>
        </ResponsiveDialog>
    </>;
};

export default EmployeePortalAccessAction;
